import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowRight,
    TrendingDown,
    Activity,
    ShieldOff,
    Wallet,
    Zap,
    Check,
    Award,
    Users,
    Compass,
    Dumbbell,
    Gauge,
    Sunrise,
    LineChart,
    BookOpen,
    Gamepad2,
    Fingerprint,
} from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/ScrollReveal";
import MagneticButton from "@/components/MagneticButton";

const SITE_URL = "https://cursus-physio.fr";

// Premium generated visuals (nano_banana) — locally hosted
const IMG_HERO_BAND = "/images/expertise-sport-sante.jpg";
const IMG_SOCIETY = "/images/expertise-society.jpg";

// Image band premium — bandeau plein largeur entre 2 sections
function ImageBand({ src, alt, testid, ratio = "16/7", tone = "ink", eager = false }) {
    return (
        <div
            className={tone === "ink" ? "relative bg-brand-ink" : "relative bg-white"}
            data-testid={testid}
        >
            <div className="mx-auto max-w-7xl px-6">
                <div
                    className="relative w-full overflow-hidden rounded-3xl"
                    style={{ aspectRatio: ratio }}
                >
                    <img
                        src={src}
                        alt={alt}
                        loading={eager ? "eager" : "lazy"}
                        fetchPriority={eager ? "high" : "auto"}
                        decoding="async"
                        className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/60 via-transparent to-transparent" />
                    <div className="absolute inset-0 bg-brand-orange/5 mix-blend-overlay" />
                </div>
            </div>
        </div>
    );
}

// ============ SECTION 1 — HERO INTRO ============
function HeroIntro() {
    const { scrollY } = useScroll();
    const overlayY = useTransform(scrollY, [0, 600], [0, 80]);

    return (
        <section
            className="relative overflow-hidden bg-brand-ink text-white"
            data-testid="expertise-hero"
        >
            <motion.div
                aria-hidden="true"
                style={{ y: overlayY }}
                className="pointer-events-none absolute -top-32 right-0 h-[640px] w-[640px] rounded-full bg-brand-orange/15 blur-[160px]"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-40 -left-20 h-[420px] w-[420px] rounded-full bg-brand-orange/[0.08] blur-[140px]"
            />
            <div className="absolute inset-0 grain pointer-events-none opacity-30" />

            <div className="relative mx-auto flex min-h-[85vh] max-w-7xl flex-col justify-center px-6 py-32 md:py-40 lg:py-48">
                <Reveal>
                    <span
                        className="inline-flex items-center self-start rounded-full border border-white/20 bg-white/[0.06] px-6 py-3 text-sm font-semibold uppercase tracking-[0.28em] text-white backdrop-blur sm:text-base"
                        data-testid="hero-eyebrow"
                    >
                        Expertise en Prévention Active
                    </span>
                </Reveal>

                <h1
                    className="mt-10 max-w-6xl font-display font-normal leading-[0.98] tracking-tightest"
                    data-testid="hero-title"
                >
                    <motion.span
                        initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
                        className="block text-[32px] sm:text-5xl lg:text-[64px] xl:text-[72px]"
                    >
                        Pourquoi les kinésithérapeutes qui choisissent
                    </motion.span>
                    <motion.span
                        initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.6 }}
                        className="block text-[32px] sm:text-5xl lg:text-[64px] xl:text-[72px]"
                    >
                        la <em className="italic text-brand-orange">Prévention Active</em> aujourd&apos;hui
                    </motion.span>
                    <motion.span
                        initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.9 }}
                        className="block text-[32px] sm:text-5xl lg:text-[64px] xl:text-[72px]"
                    >
                        construisent le <em className="italic text-brand-orange">cabinet de demain&nbsp;?</em>
                    </motion.span>
                </h1>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 1.2 }}
                    className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center"
                >
                    <MagneticButton strength={0.22}>
                        <Link
                            to="/sessions"
                            data-testid="hero-cta-sessions"
                            className="group inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-brand-orange/20 transition-shadow hover:shadow-xl hover:shadow-brand-orange/30"
                        >
                            Voir les sessions
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </MagneticButton>
                    <MagneticButton strength={0.18}>
                        <Link
                            to="/parler-conseiller"
                            data-testid="hero-cta-contact"
                            className="group inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-8 py-4 text-sm font-semibold text-white transition-all hover:border-brand-orange hover:bg-brand-orange/10"
                        >
                            Parler à un conseiller
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </MagneticButton>
                </motion.div>
            </div>
        </section>
    );
}

// ============ SECTION 2 — 3 CHIFFRES QUI CHANGENT TOUT ============
const KEY_STATS = [
    {
        value: "110 000",
        label: "kinés en France",
        note: "Un marché articulé essentiellement autour du système conventionné.",
    },
    {
        value: "58 %",
        label: "de la population souffre de TMS",
        note: "(Troubles musculo-squelettiques) 1ʳᵉ cause de maladies professionnelles indemnisées.",
    },
    {
        value: "43 %",
        label: "auront une maladie chronique en 2035",
        note: "Les prémices d'une explosion du marché de la Prévention.",
    },
];

function KeyStats() {
    return (
        <section
            className="relative bg-white py-28 md:py-36"
            data-testid="section-key-stats"
        >
            <div className="mx-auto max-w-6xl px-6">
                <Reveal className="text-center">
                    <h2 className="mx-auto max-w-4xl font-display text-[36px] font-normal leading-[1.06] tracking-tightest text-brand-ink sm:text-5xl lg:text-[64px]">
                        3 chiffres qui <em className="italic text-brand-orange">changent tout.</em>
                    </h2>
                </Reveal>

                <StaggerGroup stagger={0.15} className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
                    {KEY_STATS.map((s, i) => (
                        <StaggerItem key={s.value}>
                            <div
                                className="relative h-full overflow-hidden rounded-3xl border border-brand-ink/10 bg-[#F5F2EE] p-8 transition-all duration-500 hover:-translate-y-1 hover:border-brand-orange/30 hover:shadow-[0_30px_60px_rgba(232,135,60,0.15)] sm:p-10"
                                data-testid={`stat-card-${i}`}
                            >
                                <div className="font-display text-[54px] leading-none tracking-tightest text-brand-orange sm:text-[64px] lg:text-[72px]">
                                    {s.value}
                                </div>
                                <div className="mt-4 text-base font-semibold uppercase tracking-wide text-brand-ink sm:text-lg">
                                    {s.label}
                                </div>
                                <div className="mt-4 h-px w-16 bg-brand-orange/40" />
                                <p className="mt-4 text-sm italic leading-relaxed text-brand-ink/70 sm:text-[15px]">
                                    {s.note}
                                </p>
                            </div>
                        </StaggerItem>
                    ))}
                </StaggerGroup>

                <Reveal delay={0.4}>
                    <p className="mx-auto mt-14 max-w-3xl text-center text-base italic leading-relaxed text-brand-ink/80 sm:text-lg">
                        Avec pour paradoxe une profession sous{" "}
                        <strong className="not-italic text-brand-ink">pression économique</strong> — mais heureusement, avec des{" "}
                        <strong className="not-italic text-brand-orange">opportunités stratégiques</strong> à saisir.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

// ============ SECTION 3 — ENJEUX SOCIÉTAUX ============
const SOCIETAL_BULLETS = [
    {
        text: "58 % des femmes et 51 % des hommes de 18-64 ans déclarent des TMS.",
        source: "Santé Publique France",
        icon: Activity,
    },
    {
        text: "Les TMS sont la 1ʳᵉ cause de maladies professionnelles indemnisées en France.",
        source: null,
        icon: Award,
    },
    {
        text: "Demande préventive en hausse constante chez les actifs et sportifs amateurs.",
        source: "Rapport Kinvent, 2025",
        icon: TrendingDown,
    },
];

function EnjeuxSocietaux() {
    return (
        <section
            className="relative bg-[#F5F2EE] py-28 md:py-36"
            data-testid="section-enjeux-societaux"
        >
            <div className="mx-auto max-w-6xl px-6">
                <Reveal className="text-center">
                    <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                        Contexte
                    </span>
                    <h2 className="mx-auto mt-8 max-w-4xl font-display text-[32px] font-normal leading-[1.08] tracking-tightest text-brand-ink sm:text-[42px] lg:text-[56px]">
                        Une société qui veut rester{" "}
                        <em className="italic text-brand-orange">en forme, longtemps.</em>
                    </h2>
                    <p className="mx-auto mt-8 max-w-3xl text-base leading-[1.75] text-brand-ink/80 sm:text-[17px]">
                        Au-delà des prises en charge thérapeutiques, les Français souhaitent rester performants
                        dans leur vie quotidienne, au travail, au sein de leur famille, dans leurs activités
                        physiques ou sportives — atteints du phénomène sociétal :{" "}
                        <strong className="text-brand-ink">retarder le vieillissement.</strong>
                    </p>
                </Reveal>

                <StaggerGroup stagger={0.13} className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3">
                    {SOCIETAL_BULLETS.map((b, i) => {
                        const Icon = b.icon;
                        return (
                            <StaggerItem key={i}>
                                <div className="flex h-full flex-col rounded-2xl border border-brand-ink/10 bg-white p-6 sm:p-7">
                                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange">
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <p className="mt-5 text-[15px] font-medium leading-relaxed text-brand-ink sm:text-base">
                                        {b.text}
                                    </p>
                                    {b.source && (
                                        <p className="mt-4 text-xs italic text-brand-ink/50">
                                            Source : {b.source}
                                        </p>
                                    )}
                                </div>
                            </StaggerItem>
                        );
                    })}
                </StaggerGroup>
            </div>
        </section>
    );
}

// ============ SECTION 4 — ENJEUX ÉCONOMIQUES ============
const ECON_BULLETS = [
    {
        text: "Revalorisations tarifaires suspendues en 2025, à nouveau menacées en 2026.",
        source: "FFMKR",
    },
    { text: "ONDAM 2026 prévu à moins de 2 % d'évolution.", source: null },
    {
        text: "Revenu net moyen : 40 701 € / an pour un CA de 85 282 €, soit 52 % de charges.",
        source: "CNOMK, 2026",
    },
    {
        text: "28,3 % de diplômés étrangers en exercice : concurrence accrue, revenus comprimés.",
        source: null,
    },
];

function EnjeuxEconomiques() {
    return (
        <section
            className="relative bg-white py-28 md:py-36"
            data-testid="section-enjeux-economiques"
        >
            <div className="mx-auto max-w-6xl px-6">
                <Reveal className="text-center">
                    <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                        Pression économique
                    </span>
                    <h2 className="mx-auto mt-8 max-w-4xl font-display text-[32px] font-normal leading-[1.08] tracking-tightest text-brand-ink sm:text-[42px] lg:text-[56px]">
                        La Sécurité Sociale se retire.
                        <br className="hidden lg:block" />{" "}
                        <em className="italic text-brand-orange">Le libéral doit s&apos;adapter.</em>
                    </h2>
                </Reveal>

                <StaggerGroup stagger={0.12} className="mx-auto mt-14 grid max-w-5xl grid-cols-1 gap-4 md:grid-cols-2">
                    {ECON_BULLETS.map((b, i) => (
                        <StaggerItem key={i}>
                            <div className="flex h-full items-start gap-4 rounded-2xl border border-brand-ink/10 bg-[#F5F2EE] p-6">
                                <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange">
                                    <ShieldOff className="h-4 w-4" />
                                </span>
                                <div className="flex-1">
                                    <p className="text-[15px] font-medium leading-relaxed text-brand-ink">
                                        {b.text}
                                    </p>
                                    {b.source && (
                                        <p className="mt-2 text-xs italic text-brand-ink/50">
                                            Source : {b.source}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </StaggerItem>
                    ))}
                </StaggerGroup>
            </div>
        </section>
    );
}

// ============ SECTION 5 — CITATION PUNCH ============
function PunchQuote() {
    return (
        <section
            className="relative overflow-hidden bg-[#33333A] py-32 text-white md:py-44 grain"
            data-testid="section-punch-quote"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-brand-orange/15 blur-[160px]"
            />
            <div className="relative mx-auto max-w-5xl px-6 text-center">
                <Reveal>
                    <blockquote className="font-display text-[32px] italic leading-[1.15] text-white sm:text-5xl lg:text-[64px] lg:leading-[1.12]">
                        <span className="mr-2 font-normal text-brand-orange">«</span>
                        Attendre que la situation s&apos;améliore n&apos;est plus une{" "}
                        <em className="italic text-brand-orange">stratégie viable.</em>
                        <span className="ml-2 font-normal text-brand-orange">»</span>
                    </blockquote>
                </Reveal>
            </div>
        </section>
    );
}

// ============ SECTION 6 — LE SPORT-SANTÉ ============
const SPORT_SANTE_BULLETS = [
    {
        text: "Stoppez la perte sèche de CA + perte de légitimité perçue sur le segment Sport-Santé.",
        icon: TrendingDown,
    },
    {
        text: "Anticipez le PLFSS 2026 : « Les kinés prescrivent l'activité physique adaptée ».",
        icon: Zap,
    },
    {
        text: "Surfez sur la Stratégie Nationale Sport-Santé 2025-2030 — Appels à projets dès 2026 sur tout le territoire.",
        icon: Compass,
    },
];

function SportSante() {
    return (
        <section
            className="relative bg-[#F5F2EE] py-28 md:py-36"
            data-testid="section-sport-sante"
        >
            <div className="mx-auto max-w-6xl px-6">
                <Reveal className="text-center">
                    <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                        Marché en captation
                    </span>
                    <h2 className="mx-auto mt-8 max-w-5xl font-display text-[32px] font-normal leading-[1.08] tracking-tightest text-brand-ink sm:text-[42px] lg:text-[52px]">
                        Le Sport-Santé,
                        <br className="hidden lg:block" />{" "}
                        un marché pris par une profession{" "}
                        <em className="italic text-brand-orange">illégitime sur le plan médical.</em>
                    </h2>
                    <p className="mx-auto mt-8 max-w-3xl text-base leading-[1.75] text-brand-ink/80 sm:text-[17px]">
                        Les coachs sportifs — plus ou moins diplômés — se partagent le terrain du{" "}
                        <strong className="text-brand-ink">Sport-Santé</strong>, donc de la prévention des blessures
                        et de l&apos;accompagnement post-rééducation. Vos patients sortent du cabinet après la
                        rééducation parce que <strong className="text-brand-ink">personne ne leur propose la suite</strong>.
                        Savez-vous que vous êtes l&apos;acteur prioritaire d&apos;une approche globale de vos patients
                        et de vos futurs « clients » ?
                    </p>
                </Reveal>

                <StaggerGroup stagger={0.13} className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3">
                    {SPORT_SANTE_BULLETS.map((b, i) => {
                        const Icon = b.icon;
                        return (
                            <StaggerItem key={i}>
                                <div className="flex h-full flex-col rounded-2xl border border-brand-orange/20 bg-white p-6 sm:p-7">
                                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-orange text-white shadow-md">
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <p className="mt-5 text-[15px] font-medium leading-relaxed text-brand-ink sm:text-base">
                                        {b.text}
                                    </p>
                                </div>
                            </StaggerItem>
                        );
                    })}
                </StaggerGroup>
            </div>
        </section>
    );
}

// ============ SECTION 7 — LE MOMENT D'AGIR ============
const AGIR_CARDS = [
    {
        icon: Wallet,
        title: "Sécuriser ses revenus",
        text: "Une expertise hors nomenclature indépendante des aléas conventionnels, avec des tarifs libres à la hauteur de la profession kiné.",
    },
    {
        icon: Fingerprint,
        title: "Différencier son cabinet",
        text: "Enrichissez votre image avec un Label et une communauté leader d'opinion reconnus. Image d'expert, attractivité renforcée.",
    },
    {
        icon: Award,
        title: "Reprendre le leadership du Sport-Santé",
        text: "Passez de la patientèle à la clientèle grâce à votre légitimité médicale et des supports CURSUS PHYSIO que les coachs sportifs n'auront jamais.",
    },
];

function MomentAgir() {
    return (
        <section
            className="relative bg-white py-28 md:py-36"
            data-testid="section-moment-agir"
        >
            <div className="mx-auto max-w-6xl px-6">
                <Reveal className="text-center">
                    <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                        Timing
                    </span>
                    <h2 className="mx-auto mt-8 max-w-4xl font-display text-[36px] font-normal leading-[1.06] tracking-tightest text-brand-ink sm:text-[48px] lg:text-[64px]">
                        Le moment d&apos;agir,
                        <br className="hidden lg:block" />{" "}
                        <em className="italic text-brand-orange">c&apos;est maintenant.</em>
                    </h2>
                    <p className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-brand-ink/75 sm:text-lg">
                        Les kinésithérapeutes qui réussiront demain travailleront{" "}
                        <strong className="text-brand-ink">différemment, pas plus.</strong>
                    </p>
                </Reveal>

                <StaggerGroup stagger={0.15} className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
                    {AGIR_CARDS.map((c) => {
                        const Icon = c.icon;
                        return (
                            <StaggerItem key={c.title}>
                                <div className="group flex h-full flex-col rounded-3xl border border-brand-ink/10 bg-[#F5F2EE] p-8 transition-all duration-500 hover:-translate-y-1 hover:border-brand-orange/30 hover:shadow-[0_30px_60px_rgba(232,135,60,0.12)] sm:p-10">
                                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange text-white shadow-lg">
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <h3 className="mt-8 font-display text-2xl font-normal leading-tight text-brand-ink sm:text-[26px]">
                                        {c.title}
                                    </h3>
                                    <p className="mt-4 text-[15px] leading-relaxed text-brand-ink/70 sm:text-base">
                                        {c.text}
                                    </p>
                                </div>
                            </StaggerItem>
                        );
                    })}
                </StaggerGroup>
            </div>
        </section>
    );
}

// ============ SECTION 8 — DEVENIR EXPERT PA (bandeau grand titre) ============
function ExpertBanner() {
    return (
        <section
            className="relative overflow-hidden bg-[#F5F2EE] py-24 md:py-32"
            data-testid="section-expert-banner"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-40 right-0 h-[420px] w-[420px] rounded-full bg-brand-orange/[0.08] blur-[140px]"
            />
            <div className="relative mx-auto max-w-6xl px-6 text-center">
                <Reveal>
                    <h2 className="mx-auto max-w-5xl font-display text-[32px] font-normal leading-[1.1] tracking-tightest text-brand-ink sm:text-[42px] lg:text-[56px] xl:text-[64px]">
                        Devenir Expert en Prévention Active :
                        <br className="hidden lg:block" />
                        <span className="mt-4 block">
                            un <em className="italic text-brand-orange">savoir</em>, une{" "}
                            <em className="italic text-brand-orange">identité</em>, une{" "}
                            <em className="italic text-brand-orange">communauté</em>, une{" "}
                            <em className="italic text-brand-orange">force</em>.
                        </span>
                    </h2>
                </Reveal>
            </div>
        </section>
    );
}

// ============ SECTION 9 — 3 PILIERS ============
const PILIERS = [
    {
        n: "01",
        title: "Une légitimité médicale irréfutable",
        text: "CURSUS PHYSIO, c'est un programme de formations évolutives concernant tous les accompagnements préventifs que seuls les kinésithérapeutes sont à même d'anticiper.",
    },
    {
        n: "02",
        title: "Une communauté d'experts plébiscitée au niveau national",
        text: "CURSUS PHYSIO fédère et anime un réseau actif de kinésithérapeutes partageant méthodes et protocoles validés, retours d'expérience et innovations cliniques. Un positionnement collectif fort.",
    },
    {
        n: "03",
        title: "Une indépendance additionnelle au système conventionné",
        text: "CURSUS PHYSIO est l'opportunité de diversifier votre activité dans l'accompagnement préventif — auprès de vos patients, mais surtout auprès d'une clientèle nouvelle. Vous accompagnez désormais dans la durée, au-delà et en complément de la prise en charge thérapeutique, en totale indépendance dans le fond applicatif et la forme économique.",
    },
];

function TroisPiliers() {
    return (
        <section
            className="relative bg-white py-28 md:py-36"
            data-testid="section-piliers"
        >
            <div className="mx-auto max-w-6xl px-6">
                <Reveal className="text-center">
                    <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                        3 piliers
                    </span>
                    <h2 className="mx-auto mt-8 max-w-4xl font-display text-[36px] font-normal leading-[1.06] tracking-tightest text-brand-ink sm:text-5xl lg:text-[64px]">
                        Les 3 piliers du{" "}
                        <em className="italic text-brand-orange">statut d&apos;Expert.</em>
                    </h2>
                </Reveal>

                <StaggerGroup stagger={0.14} className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
                    {PILIERS.map((p) => (
                        <StaggerItem key={p.n}>
                            <div
                                className="group flex h-full flex-col rounded-3xl border border-brand-ink/10 bg-[#F5F2EE] p-8 transition-all duration-500 hover:-translate-y-1 hover:border-brand-orange/30 sm:p-10"
                                data-testid={`pilier-${p.n}`}
                            >
                                <span className="font-display text-4xl italic text-brand-orange sm:text-5xl">
                                    {p.n}
                                </span>
                                <h3 className="mt-6 font-display text-xl font-normal leading-tight text-brand-ink sm:text-[22px]">
                                    {p.title}
                                </h3>
                                <p className="mt-4 text-[15px] leading-relaxed text-brand-ink/70 sm:text-base">
                                    {p.text}
                                </p>
                            </div>
                        </StaggerItem>
                    ))}
                </StaggerGroup>
            </div>
        </section>
    );
}

// ============ SECTION 10 — MODULES SHOWCASE (SOMA / NEURO-KINESIS / VITAL) ============
const MODULES = [
    {
        id: "soma",
        n: "01",
        code: "SOMA",
        baseline: "Maîtriser le corps en mouvement : de la structure à la performance.",
        icon: Dumbbell,
        color: "from-brand-orange/25 to-brand-orange/5",
        image: "/images/module-soma.jpg",
        description:
            "Ce module plonge le kinésithérapeute au cœur de la biomécanique vivante. De la prévention des lombalgies à la récupération post-opératoire hanche/genou, en passant par l'optimisation du renforcement musculaire par l'isométrie et la lutte contre la sarcopénie et l'ostéoporose, SOMA forme à une approche structurelle et fonctionnelle du corps. Ici, le praticien ne traite plus seulement la douleur — il restaure, renforce et protège l'appareil locomoteur dans sa globalité, à chaque étape de la vie.",
        themes: [
            "Prévention active des lombalgies (prise en charge autonome et durable)",
            "Prévention des complications post-opératoires Hanche / Genou",
            "Optimisation du renforcement musculaire & isométrie",
            "Sarcopénie active & Ostéoporose",
        ],
    },
    {
        id: "neuro",
        n: "02",
        code: "NEURO-KINESIS",
        baseline:
            "Évaluer, mesurer, décider : la kinésithérapie à l'ère de la donnée neuro-motrice.",
        icon: Gauge,
        color: "from-brand-orange/20 to-brand-orange/5",
        image: "/images/module-neuro.jpg",
        description:
            "Ce module repositionne le kinésithérapeute comme un expert de l'évaluation quantitative et du mouvement réflexe. En intégrant les outils de mesure de l'activité musculaire, l'analyse de la douleur post-opératoire et les concepts d'ergomotricité et d'ostéopathie fonctionnelle (OrthoBioKinésie), NEURO-KINESIS permet de passer d'une pratique intuitive à une pratique basée sur la preuve et le ressenti objectivé, de l'enfant au senior.",
        themes: [
            "Thérapie réflexe, ostéopathie & ergomotricité — concept OrthoBioKinésie",
            "Évaluation quantitative de l'Enfant au Senior (mesure musculaire, angle douleur)",
            "Outils d'évaluation objective",
        ],
    },
    {
        id: "vital",
        n: "03",
        code: "VITAL",
        baseline:
            "Optimiser la vie pour durer : sommeil, récupération, nutrition et longévité active.",
        icon: Sunrise,
        color: "from-brand-orange/20 to-brand-orange/5",
        image: "/images/module-vital.jpg",
        description:
            "Ce module ouvre la kinésithérapie à sa dimension la plus humaine et la plus systémique. Le praticien y apprend à intégrer dans son accompagnement les leviers invisibles mais déterminants de la santé durable : qualité du sommeil, gestion du stress, récupération sportive et médecine anti-âge par le biohacking. VITAL transforme le kiné en véritable coach de longévité active, capable d'accompagner ses patients bien au-delà de la séance de soin.",
        themes: [
            "Sommeil, repos & récupération dans le sport",
            "Biohacking & médecine anti-âge",
            "Gestion du stress & nutrition (leviers complémentaires)",
        ],
    },
];

function ModulesShowcase() {
    return (
        <section
            className="relative bg-white py-28 md:py-36"
            data-testid="section-modules"
        >
            <div className="mx-auto max-w-7xl px-6">
                <Reveal className="text-center">
                    <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                        Le programme
                    </span>
                    <h2 className="mx-auto mt-8 max-w-5xl font-display text-[32px] font-normal leading-[1.06] tracking-tightest text-brand-ink sm:text-[42px] lg:text-[56px]">
                        Un plan de formation évolutif,
                        <br className="hidden lg:block" />{" "}
                        <em className="italic text-brand-orange">ancré dans votre pratique.</em>
                    </h2>
                    <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-brand-ink/70 sm:text-lg">
                        Adapté au spectre de l&apos;expertise Prévention Active.
                    </p>
                </Reveal>

                <div className="mt-20 space-y-14">
                    {MODULES.map((m, idx) => {
                        const Icon = m.icon;
                        const isEven = idx % 2 === 0;
                        return (
                            <Reveal key={m.id} delay={0.1}>
                                <article
                                    className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-stretch lg:gap-14"
                                    data-testid={`module-${m.id}`}
                                >
                                    {/* Visuel */}
                                    <div className={`lg:col-span-5 ${isEven ? "" : "lg:order-2"}`}>
                                        <div className="relative aspect-[4/5] w-full overflow-hidden rounded-3xl bg-brand-ink">
                                            <img
                                                src={m.image}
                                                alt={`Module ${m.code}`}
                                                loading="lazy"
                                                decoding="async"
                                                className="h-full w-full object-cover opacity-80 transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:scale-105"
                                            />
                                            <div className={`absolute inset-0 bg-gradient-to-b ${m.color}`} />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                            <div className="absolute left-6 top-6 flex items-center gap-3">
                                                <span className="font-display text-5xl italic text-white/90 sm:text-6xl">
                                                    {m.n}
                                                </span>
                                                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-orange text-white shadow-lg">
                                                    <Icon className="h-5 w-5" />
                                                </span>
                                            </div>
                                            <div className="absolute inset-x-0 bottom-0 p-6">
                                                <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white/80">
                                                    Module {m.n}
                                                </div>
                                                <div className="mt-2 font-display text-3xl italic text-white sm:text-4xl">
                                                    {m.code}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contenu */}
                                    <div className={`flex flex-col justify-center lg:col-span-7 ${isEven ? "" : "lg:order-1"}`}>
                                        <p className="font-display text-xl italic leading-snug text-brand-orange sm:text-2xl lg:text-[28px]">
                                            « {m.baseline} »
                                        </p>
                                        <p className="mt-6 text-[15px] leading-[1.75] text-brand-ink/80 sm:text-base">
                                            {m.description}
                                        </p>

                                        <div className="mt-8 rounded-2xl border border-brand-ink/10 bg-[#F5F2EE] p-6">
                                            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-orange">
                                                Thématiques intégrées
                                            </div>
                                            <ul className="mt-4 space-y-3">
                                                {m.themes.map((t) => (
                                                    <li
                                                        key={t}
                                                        className="flex items-start gap-3 text-[14px] leading-relaxed text-brand-ink/85 sm:text-[15px]"
                                                    >
                                                        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange">
                                                            <Check className="h-3 w-3" strokeWidth={3} />
                                                        </span>
                                                        <span>{t}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </article>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// ============ SECTION 11 — ÉCOSYSTÈME OUTILS ============
const TOOLS = [
    {
        icon: LineChart,
        title: "Outil de diagnostic optimisé",
        text: "Protocoles d'évaluation standardisés et scorés. Objectiver le risque locomoteur en quelques minutes. Justification clinique solide.",
    },
    {
        icon: BookOpen,
        title: "Bibliothèque de protocoles personnalisables",
        text: "Programmes prêts à l'emploi, adaptables selon âge, pathologie, niveau d'activité. Moins de préparation, plus de valeur ajoutée.",
    },
    {
        icon: Gamepad2,
        title: "Application patient gamifiée",
        text: "Exercices en cabinet ou à domicile dans un environnement ludique et motivant. Progression mesurable, défis, points. Meilleure observance, patients fidèles et ambassadeurs.",
    },
];

function EcosystemeOutils() {
    return (
        <section
            className="relative bg-[#F5F2EE] py-28 md:py-36"
            data-testid="section-outils"
        >
            <div className="mx-auto max-w-6xl px-6">
                <Reveal className="text-center">
                    <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                        Outils propriétaires
                    </span>
                    <h2 className="mx-auto mt-8 max-w-5xl font-display text-[32px] font-normal leading-[1.08] tracking-tightest text-brand-ink sm:text-[42px] lg:text-[52px]">
                        Un écosystème d&apos;outils exclusifs
                        <br className="hidden lg:block" />{" "}
                        <em className="italic text-brand-orange">
                            qui complète votre pratique quotidienne en cabinet.
                        </em>
                    </h2>
                </Reveal>

                <StaggerGroup stagger={0.13} className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
                    {TOOLS.map((t) => {
                        const Icon = t.icon;
                        return (
                            <StaggerItem key={t.title}>
                                <div className="group flex h-full flex-col rounded-3xl border border-brand-ink/10 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-brand-orange/30 hover:shadow-[0_30px_60px_rgba(232,135,60,0.12)] sm:p-10">
                                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange text-white shadow-lg">
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <h3 className="mt-8 font-display text-xl font-normal leading-tight text-brand-ink sm:text-[22px]">
                                        {t.title}
                                    </h3>
                                    <p className="mt-4 text-[15px] leading-relaxed text-brand-ink/70 sm:text-base">
                                        {t.text}
                                    </p>
                                </div>
                            </StaggerItem>
                        );
                    })}
                </StaggerGroup>
            </div>
        </section>
    );
}

// ============ SECTION 12 — L'ÉQUATION ÉCONOMIQUE ============
const COMPARE_ROWS = [
    { label: "Tarification", conv: "Plafonnée par la Sécurité Sociale", pa: "Tarif libre / non plafonné" },
    { label: "Dépendance", conv: "Prescription + Sécu", pa: "Autonomie totale" },
    { label: "Engagement patient", conv: "Passif dans la rééducation", pa: "Actif, gamifié, impliqué" },
    { label: "Fidélisation", conv: "Épisodique", pa: "Continue / abonnements périodiques" },
    { label: "Image cabinet", conv: "Standardisée", pa: "Label Expert / Référent local" },
];

function EquationEconomique() {
    return (
        <section
            className="relative bg-white py-28 md:py-36"
            data-testid="section-equation"
        >
            <div className="mx-auto max-w-6xl px-6">
                <Reveal className="text-center">
                    <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                        Modèle économique
                    </span>
                    <h2 className="mx-auto mt-8 max-w-4xl font-display text-[36px] font-normal leading-[1.06] tracking-tightest text-brand-ink sm:text-5xl lg:text-[64px]">
                        L&apos;équation <em className="italic text-brand-orange">économique.</em>
                    </h2>
                </Reveal>

                {/* Tableau desktop */}
                <Reveal delay={0.2}>
                    <div
                        className="mt-14 hidden overflow-hidden rounded-3xl border border-brand-ink/10 md:block"
                        data-testid="compare-table"
                    >
                        <div className="grid grid-cols-[1.1fr_1fr_1fr] bg-brand-ink text-white">
                            <div className="p-5 text-xs font-semibold uppercase tracking-[0.22em] text-white/80">
                                Critère
                            </div>
                            <div className="p-5 text-xs font-semibold uppercase tracking-[0.22em] text-white/60">
                                Activité conventionnée
                            </div>
                            <div className="p-5 text-xs font-semibold uppercase tracking-[0.22em] text-brand-orange">
                                Prévention Active
                            </div>
                        </div>
                        {COMPARE_ROWS.map((r, i) => (
                            <div
                                key={r.label}
                                className={`grid grid-cols-[1.1fr_1fr_1fr] border-t border-brand-ink/10 ${
                                    i % 2 === 0 ? "bg-white" : "bg-[#F5F2EE]"
                                }`}
                            >
                                <div className="p-5 font-display text-lg text-brand-ink">{r.label}</div>
                                <div className="p-5 text-sm text-brand-ink/70">{r.conv}</div>
                                <div className="p-5 text-sm font-medium text-brand-ink">{r.pa}</div>
                            </div>
                        ))}
                    </div>
                </Reveal>

                {/* Cards mobile */}
                <div className="mt-14 grid grid-cols-1 gap-4 md:hidden">
                    {COMPARE_ROWS.map((r) => (
                        <div
                            key={r.label}
                            className="rounded-2xl border border-brand-ink/10 bg-white p-5"
                        >
                            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-orange">
                                {r.label}
                            </div>
                            <div className="mt-3 grid grid-cols-1 gap-2 text-sm">
                                <div className="flex items-start gap-2 rounded-xl bg-[#F5F2EE] p-3">
                                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-ink/50">
                                        Convention
                                    </span>
                                    <span className="text-brand-ink/80">{r.conv}</span>
                                </div>
                                <div className="flex items-start gap-2 rounded-xl border border-brand-orange/20 bg-brand-orange/5 p-3">
                                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-orange">
                                        PA
                                    </span>
                                    <span className="font-medium text-brand-ink">{r.pa}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <Reveal delay={0.4}>
                    <div className="mt-14 space-y-6 text-center">
                        <p className="font-display text-2xl italic leading-snug text-brand-orange sm:text-3xl lg:text-[32px]">
                            = Chaque patient en fin de rééducation = candidat naturel à un programme de prévention.
                        </p>
                        <p className="font-display text-2xl italic leading-snug text-brand-orange sm:text-3xl lg:text-[32px]">
                            = Chaque sportif suivi = ambassadeur potentiel auprès de son réseau.
                        </p>
                        <p className="font-display text-2xl italic leading-snug text-brand-orange sm:text-3xl lg:text-[32px]">
                            = Fidélisation de la patientèle = ouverture sur une clientèle.
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

// ============ SECTION 13 — REVENU HORS CONVENTION ============
function RevenuHorsConvention() {
    return (
        <section
            className="relative overflow-hidden bg-[#33333A] py-28 text-white md:py-36 grain"
            data-testid="section-revenu"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-brand-orange/15 blur-[160px]"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-40 left-0 h-[420px] w-[420px] rounded-full bg-brand-orange/10 blur-[140px]"
            />

            <div className="relative mx-auto max-w-5xl px-6">
                <Reveal className="text-center">
                    <span className="inline-flex items-center rounded-full border border-white/25 bg-white/5 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-white/90 backdrop-blur-sm">
                        Revenu additionnel
                    </span>
                    <h2 className="mx-auto mt-8 max-w-4xl font-display text-[32px] font-normal leading-[1.06] tracking-tightest sm:text-[42px] lg:text-[56px]">
                        Expert en Prévention Active,
                        <br className="hidden lg:block" />{" "}
                        <em className="italic text-brand-orange">votre nouveau revenu Hors Convention.</em>
                    </h2>
                </Reveal>

                <Reveal delay={0.2}>
                    <div className="mx-auto mt-14 max-w-3xl rounded-3xl border border-white/15 bg-white/[0.04] p-8 backdrop-blur-sm sm:p-12">
                        <div className="space-y-6">
                            <div className="flex items-baseline justify-between gap-6 border-b border-white/10 pb-5">
                                <span className="text-sm uppercase tracking-[0.18em] text-white/60">
                                    Forfait mensuel estimé
                                </span>
                                <span className="font-display text-2xl italic text-brand-orange">
                                    75 € / client
                                </span>
                            </div>
                            <div className="flex items-baseline justify-between gap-6 border-b border-white/10 pb-5">
                                <span className="text-sm uppercase tracking-[0.18em] text-white/60">
                                    Engagement minimum
                                </span>
                                <span className="font-display text-2xl italic text-brand-orange">
                                    6 mois / client
                                </span>
                            </div>
                            <div className="flex items-baseline justify-between gap-6 pb-5">
                                <span className="text-sm uppercase tracking-[0.18em] text-white/60">
                                    Temps de suivi
                                </span>
                                <span className="text-right font-display text-lg italic text-white sm:text-xl">
                                    15 à 20 min / client / mois
                                </span>
                            </div>

                            <div className="flex justify-center">
                                <ArrowRight className="h-6 w-6 rotate-90 text-brand-orange" />
                            </div>

                            <div className="text-center">
                                <div className="text-xs font-semibold uppercase tracking-[0.28em] text-white/70">
                                    Revenu additionnel potentiel
                                </div>
                                <div
                                    className="mt-4 font-display text-[48px] italic leading-[1] text-brand-orange sm:text-6xl lg:text-[88px]"
                                    data-testid="revenu-chiffre"
                                >
                                    jusqu&apos;à 3 750 €
                                </div>
                                <div className="mt-2 text-sm uppercase tracking-[0.28em] text-brand-orange/90 sm:text-base">
                                    / mois
                                </div>
                                <p className="mt-6 text-sm italic text-white/60">
                                    de revenus supplémentaires{" "}
                                    <span className="not-italic">(base 50 clients mensuels).</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.4}>
                    <p className="mx-auto mt-8 max-w-2xl text-center text-xs italic text-white/50">
                        Estimation à titre indicatif. Résultats variables selon activité, zone géographique et
                        engagement individuel.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

// ============ SECTION 14 — CTA FINAL ============
function FinalCTA() {
    return (
        <section
            className="relative overflow-hidden bg-brand-ink py-32 text-white md:py-44 grain"
            data-testid="section-final-cta"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-brand-orange/15 blur-[160px]"
            />
            <div className="relative mx-auto max-w-5xl px-6 text-center">
                <Reveal>
                    <span
                        className="inline-flex items-center rounded-full border border-white/25 bg-white/5 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-white/90 backdrop-blur-sm"
                        data-testid="final-eyebrow"
                    >
                        <Users className="mr-2 h-3.5 w-3.5 text-brand-orange" />
                        Rejoignez la 1<sup className="text-[0.55em] align-super">re</sup>&nbsp;communauté
                        d&apos;Experts
                    </span>
                    <h2 className="mx-auto mt-8 max-w-4xl font-display text-[40px] font-normal leading-[1.04] tracking-tightest sm:text-5xl lg:text-7xl xl:text-[80px] lg:leading-[1.06]">
                        Devenez <em className="italic text-brand-orange">Expert</em>
                        <br className="hidden lg:block" />{" "}
                        en Prévention Active.
                    </h2>
                    <p className="mx-auto mt-10 max-w-2xl text-lg leading-relaxed text-white/75 sm:text-xl">
                        Rejoignez la{" "}
                        <em className="italic text-brand-orange">
                            1<sup className="text-[0.55em] align-super">re</sup>&nbsp;communauté
                        </em>{" "}
                        d&apos;Experts en Prévention Active.
                    </p>
                </Reveal>

                <Reveal delay={0.2}>
                    <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
                        <MagneticButton strength={0.25}>
                            <Link
                                to="/sessions"
                                data-testid="final-cta-sessions"
                                className="group inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-brand-orange/20 transition-shadow hover:shadow-xl hover:shadow-brand-orange/30"
                            >
                                Voir les prochaines sessions
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </MagneticButton>
                        <MagneticButton strength={0.18}>
                            <Link
                                to="/parler-conseiller"
                                data-testid="final-cta-contact"
                                className="group inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-8 py-4 text-sm font-semibold text-white transition-all hover:border-brand-orange hover:bg-brand-orange/10"
                            >
                                Parler à un conseiller
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </MagneticButton>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

// ============ PAGE ============
export default function ExpertisePA() {
    return (
        <div data-testid="page-expertise-pa">
            <Helmet>
                <title>Expertise en Prévention Active — Cursus Physio</title>
                <meta
                    name="description"
                    content="Devenez Expert en Prévention Active (EPA). 3 modules (SOMA, NEURO-KINESIS, VITAL), un revenu Hors Convention jusqu'à 3 750 €/mois, une communauté nationale."
                />
                <link rel="canonical" href={`${SITE_URL}/expertise-pa`} />
                <meta property="og:title" content="Expertise en Prévention Active — Cursus Physio" />
                <meta
                    property="og:description"
                    content="Le cabinet de demain se construit aujourd'hui. Devenez Expert en Prévention Active."
                />
                <meta property="og:url" content={`${SITE_URL}/expertise-pa`} />
            </Helmet>

            <HeroIntro />
            <ImageBand
                src={IMG_HERO_BAND}
                alt="Kinésithérapeute en coaching squat avec un client — Sport-Santé actif"
                testid="expertise-hero-image-band"
                ratio="16/7"
                tone="ink"
                eager
            />
            <KeyStats />
            <ImageBand
                src={IMG_SOCIETY}
                alt="Groupe divers en exercices matinaux au parc — société active"
                testid="expertise-society-image-band"
                ratio="21/9"
                tone="light"
            />
            <EnjeuxSocietaux />
            <EnjeuxEconomiques />
            <PunchQuote />
            <SportSante />
            <MomentAgir />
            <ExpertBanner />
            <TroisPiliers />
            <ModulesShowcase />
            <EcosystemeOutils />
            <EquationEconomique />
            <RevenuHorsConvention />
            <FinalCTA />
        </div>
    );
}
